import { memo, useEffect, useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataTable from "src/components/common/DataTables/DataTable";
import BulkStatusCell from "./BulkStatusCell";
import { BULK_BUCKETS, BULK_STATUS } from "./constants";

const PAGE_SIZE = 10;

const BUCKET_FILTERS = {
  [BULK_BUCKETS.all]: () => true,
  [BULK_BUCKETS.assigned]: (row) =>
    row.status === BULK_STATUS.assigned ||
    row.status === BULK_STATUS.alreadyAssigned,
  [BULK_BUCKETS.failed]: (row) => row.status === BULK_STATUS.failed,
  [BULK_BUCKETS.unsupported]: (row) => row.status === BULK_STATUS.unsupported,
  [BULK_BUCKETS.waiting]: (row) =>
    [
      BULK_STATUS.waiting,
      BULK_STATUS.checking,
      BULK_STATUS.eligible,
      BULK_STATUS.assigning,
      BULK_STATUS.stopped,
    ].includes(row.status),
};

/**
 * The live run table (steps 2-4). `DataTable` hardcodes `disableVirtualization`,
 * so rows are bucketed and paged client-side rather than handing it 100+ rows.
 */
function BulkRunTable({ rows, counts, companyName, height = 380 }) {
  const { t } = useTranslation();
  const [bucket, setBucket] = useState(BULK_BUCKETS.all);
  const [page, setPage] = useState(1);

  const bucketRows = useMemo(
    () => rows.filter(BUCKET_FILTERS[bucket] ?? BUCKET_FILTERS.all),
    [rows, bucket],
  );

  // A bucket can shrink under the current page while the run progresses.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(bucketRows.length / PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [bucketRows.length, page]);

  const pagedRows = useMemo(
    () => bucketRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [bucketRows, page],
  );

  const columns = useMemo(
    () => [
      {
        field: "orderNumber",
        headerName: t("Order No."),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography noWrap variant="subtitle2">
            {value ?? "-"}
          </Typography>
        ),
      },
      {
        field: "route",
        headerName: t("Route"),
        flex: 1.2,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography noWrap variant="subtitle2">
            {`${row.origin ?? "-"} - ${row.destination ?? "-"}`}
          </Typography>
        ),
      },
      {
        field: "weight",
        headerName: t("Chargeable Weight"),
        flex: 0.7,
        minWidth: 100,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography noWrap variant="subtitle2">
            {value ? `${value} ${t("kg")}` : "-"}
          </Typography>
        ),
      },
      {
        field: "price",
        headerName: t("Price"),
        flex: 0.7,
        minWidth: 90,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography noWrap variant="subtitle2">
            {Number.isFinite(value) ? value : "-"}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: t("Status"),
        flex: 1.8,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <BulkStatusCell
            status={row.status}
            errorMsg={row.errorMsg}
            companyName={companyName}
            currentStatus={row.currentStatus}
          />
        ),
      },
    ],
    [companyName, t],
  );

  const tabs = [
    { value: BULK_BUCKETS.all, label: t("All"), count: counts.total },
    {
      value: BULK_BUCKETS.waiting,
      label: t("bulkAssign.status.waiting"),
      count:
        counts.waiting +
        counts.checking +
        counts.eligible +
        counts.assigning +
        counts.stopped,
    },
    {
      value: BULK_BUCKETS.assigned,
      label: t("bulkAssign.status.assigned"),
      count: counts.assigned + counts.alreadyAssigned,
    },
    {
      value: BULK_BUCKETS.failed,
      label: t("bulkAssign.status.failed"),
      count: counts.failed,
    },
    {
      value: BULK_BUCKETS.unsupported,
      label: t("Not supported"),
      count: counts.unsupported,
    },
  ];

  return (
    <Box>
      <Tabs
        value={bucket}
        onChange={(_, value) => {
          setBucket(value);
          setPage(1);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: 40 }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            sx={{ textTransform: "none", minHeight: 40 }}
            label={`${tab.label} (${tab.count})`}
          />
        ))}
      </Tabs>

      <Box sx={{ height }}>
        <DataTable
          tableId="BULK-ASSIGN-RUN-TABLE"
          rows={pagedRows}
          columns={columns}
          getRowId={(row) => row.orderId}
          rowCount={bucketRows.length}
          pageSize={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          page={page}
          onPageChange={setPage}
          customComponents={["Pagination"]}
          checkboxSelection={false}
          disableColumnFilter
          disableColumnSelector
        />
      </Box>
    </Box>
  );
}

export default memo(BulkRunTable);
